import { MD5 } from 'crypto-js';
import slugify from 'react-slugify';
import { z } from 'zod';

const schema = z.object({
  email: z.string().email('Invalid email').min(1, 'Email is required').toLowerCase(),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  userName: z.string().min(1, 'Username is required').max(100),
  jobRole: z.string().min(1, 'Job Title is required').max(100), // <-- add this line
  shouldSendEmail: z.boolean().optional(),
  role: z.union([
    z.literal('admin'),
    z.literal('administrator'),
    z.literal('user'),
    z.literal('user_sales'),
    z.literal('user_sales_restricted'),
  ]),
  slug: z.string().max(150).optional(),
  displayName: z.string().min(1, 'Display name is required').max(100).optional(),
});

const refinedSchema = schema.refine(
  ({ slug }) => {
    return slug === slugify(slug);
  },
  {
    message: 'Slug must be a valid URL slug',
    path: ['slug'],
  }
);

const passwordSchema = z.object({
  password: z.string().min(1, 'Password is required').max(100),
  confirmPassword: z.string(),
});

const passwordRefinedSchema = passwordSchema
  .refine(
    ({ password, confirmPassword }) => {
      return MD5(password).toString() === MD5(confirmPassword).toString();
    },
    {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    }
  )
  .refine(
    ({ password }) => {
      return (
        password.length >= 6 &&
        password.length <= 100 &&
        password.match(/[a-z]/) &&
        password.match(/[A-Z]/) &&
        password.match(/[0-9]/) &&
        password.match(/[^a-zA-Z\d]/)
      );
    },
    {
      message:
        'Password must be between 6 and 100 characters and contain at least one lowercase letter, one uppercase letter, one number, and one special character',
      path: ['password'],
    }
  );

const createSchema = z.object({
  ...passwordSchema.shape,
  ...schema.shape,
});

const createRefinedSchema = z.intersection(refinedSchema, passwordRefinedSchema);

const DEFAULTVALUES: UserFormData = {
  email: '',
  firstName: '',
  lastName: '',
  userName: '',
  jobRole: '',
  password: '',
  confirmPassword: '',
  role: 'user',
  shouldSendEmail: true,
  slug: '',
  meta: {
    profileImage: '', // default value for profileImage
    description: '', // default value for description
  },
};

export type UserFormData = z.infer<typeof schema> &
  z.infer<typeof createSchema> & {
    profileImage?: string;
    description?: string;
    meta?: {
      profileImage?: string;
      description?: string;
    };
  };

export type UserPasswordSchema = z.infer<typeof passwordSchema>;

export {
  schema as userSchema,
  refinedSchema as userRefinedSchema,
  createSchema as userCreateSchema,
  createRefinedSchema as userCreateRefinedSchema,
  passwordSchema as userPasswordSchema,
  passwordRefinedSchema as userPasswordRefinedSchema,
  DEFAULTVALUES as UserDefaultValues,
};
